// One way for every marketing form to get a reCAPTCHA v3 token.
//
// react-google-recaptcha-v3 hands out `executeRecaptcha` only once Google's
// script has loaded; until then it is undefined. The forms used to send an
// empty token in that window ("executeRecaptcha ? ... : ''"), which the
// backend rejects as "reCAPTCHA token missing" - a customer on an iPad tapped
// Accept & Pay and got exactly that (production, 2026-10-05). A click handler
// also keeps the `undefined` it was created with, so it cannot simply retry.
//
// So: use the provider's function when it is ready; otherwise wait for
// window.grecaptcha itself (bounded), and only then give up with a message a
// customer can act on - a content blocker or no connection is the usual cause.

type ExecuteRecaptcha = ((action: string) => Promise<string>) | undefined;

interface Grecaptcha {
  ready: (cb: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

export const RECAPTCHA_UNAVAILABLE
  = 'We couldn\'t load the security check. Please check your connection, turn off any content blocker for this site, and try again.';

const siteKey = (): string => process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';

const grecaptchaNow = (): Grecaptcha | undefined => {

  const g = (globalThis as { grecaptcha?: Grecaptcha }).grecaptcha;
  return g && typeof g.execute === 'function' ? g : undefined;
};

const waitForGrecaptcha = async (timeoutMs: number, stepMs: number): Promise<Grecaptcha | undefined> => {

  for (let waited = 0; waited <= timeoutMs; waited += stepMs) {

    const g = grecaptchaNow();

    if (g) {

      return g;
    }

    await new Promise((resolve) => setTimeout(resolve, stepMs));
  }

  return undefined;
};

export async function getRecaptchaToken(
  executeRecaptcha: ExecuteRecaptcha,
  action: string,
  timeoutMs = 8000,
  stepMs = 250
): Promise<string> {

  // No site key: reCAPTCHA is off in this environment (local dev), and the
  // backend skips verification too - same as before.
  if (!siteKey()) {

    return '';
  }

  if (executeRecaptcha) {

    return executeRecaptcha(action);
  }

  const g = await waitForGrecaptcha(timeoutMs, stepMs);

  if (!g) {

    throw new Error(RECAPTCHA_UNAVAILABLE);
  }

  return new Promise<string>((resolve, reject) => {

    g.ready(() => {

      g.execute(siteKey(), {
        action
      }).then(resolve, reject);
    });
  });
}
